// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Custom hook for managing fixed assets rollforward data fetching and state.
 *
 * Handles data loading, error states, and provides computed values for
 * fixed asset flow calculations.
 */
import { IsoCurrencyCode } from '@/currency/constants/iso';
import { getFixedAssetsRollforwardData } from '@/report/api/get-fixed-assets-rollforward-data';
import { useRollforwardCurrencyQuery } from '@/report/hooks/use-rollforward-currency-query';
import { DateTime } from 'luxon';

interface UseFixedAssetsRollforwardDataParams {
  startDate?: string;
  endDate?: string;
  enabled?: boolean;
  activeCurrencyCode: IsoCurrencyCode;
}

export function useFixedAssetsRollforwardData(
  params: UseFixedAssetsRollforwardDataParams
) {
  const startDateTime = params.startDate
    ? DateTime.fromISO(params.startDate)
    : null;
  const endDateTime = params.endDate ? DateTime.fromISO(params.endDate) : null;

  const query = useRollforwardCurrencyQuery(
    'asset-rollforward-fixed-assets',
    startDateTime,
    endDateTime,
    params.enabled !== false,
    params.activeCurrencyCode,
    getFixedAssetsRollforwardData
  );

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
  };
}
